import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import { Grid } from '@material-ui/core';

import TaskStatusCard from '../tasks/cards/TaskStatusCard';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import { useParams } from 'react-router-dom';

import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import { Scar, BaseScarCardProps } from './Scar.interface';
import { useFindScarByTask } from './Scar.hook';
import ProblemDetailCard from './cards/ProblemDetailCard';
import PreventiveActionCard from './cards/PreventiveActionCard';
import SupplierContactCard from '../supplier/cards/SupplierContactCard';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  .status-container {
    margin-top: 5px;
  }
`;

export const ScarDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId?: string }>();
  const [scar, setScar] = useState<Scar>();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const { data, error: findError, get } = useFindScarByTask(Number(taskId));

  const scarCardProps: BaseScarCardProps = {
    scar: scar!,
    onChange: (newScar: Partial<Scar>) => {
      scar && setScar({ ...scar, ...newScar });
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!scar?.task) {
      return;
    }
    Object.assign(scar.task, newTask);
    scarCardProps.onChange?.({});
    scarCardProps.onUpdate?.();
  };

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    data && setScar(data);
  }, [data]);

  useEffect(() => {
    setError(findError);
  }, [findError]);
  const { isFieldDisabled } = useTaskEntityFields(scar?.task);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <StyledTitle>
          <h2>{scar && 'SCAR-' + scar.id}</h2>
          <div className='status-container'>
            {scar && <TaskStatusLabel task={scar.task} />}
          </div>
        </StyledTitle>
        <PageTemplateHeaderActions flex>
          {scar?.task && (
            <TaskStatusActions
              task={scar.task}
              taskEntity={scar}
              onChange={task => {
                scar.task = task;
                setScar({ ...scar });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        {scar && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {scar.supplier && (
                    <SupplierContactCard supplier={scar.supplier} />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <ProblemDetailCard {...scarCardProps} />
                </Grid>
                <Grid item xs={12}>
                  <PreventiveActionCard {...scarCardProps} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                {scar.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskStatusCard task={scar.task} />
                    </Grid>
                    {scar.task.children && (
                      <Grid item xs={12}>
                        <SubTaskListCard
                          task={scar.task}
                          title='Stakeholders'
                          disabled={isFieldDisabled('assignSubTasks')}
                          onError={scarCardProps.onError}
                          onChange={handleTaskUpdate}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TaskFileListCard
                        task={scar.task}
                        onError={scarCardProps.onError}
                        onChange={handleTaskUpdate}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>
      <AppSnackbar
        severity='error'
        open={Boolean(error)}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        SCAR details updated.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default ScarDetailPage;
